import React, { useState, useRef } from 'react';
import type { PurchaseOrderItem } from '../../types';
import {
  importPOLinesFromCSV,
  importPOLinesFromPaste,
  poAutoDetectColumns,
  poImportRowsToLineItems,
  type POImportColumnMapping,
} from '../../lib/poImportService';
import { readFileAsText } from '../../lib/shipmentImportService';

interface POPasteImportPanelProps {
  onImport: (lines: PurchaseOrderItem[]) => void;
}

const POPasteImportPanel: React.FC<POPasteImportPanelProps> = ({ onImport }) => {
  const [pasteText, setPasteText] = useState('');
  const [importError, setImportError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handlePasteImport = () => {
    setImportError('');
    const rows = importPOLinesFromPaste(pasteText);
    if (rows.length === 0) {
      setImportError('No lines found. Use: name, qty, cost per line.');
      return;
    }
    onImport(poImportRowsToLineItems(rows));
    setPasteText('');
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError('');
    try {
      const content = await readFileAsText(file);
      const lines = content.split(/\r?\n/).filter(Boolean);
      const headers = lines[0]?.split(',').map((h) => h.trim().replace(/"/g, '')) ?? [];
      const mapping: POImportColumnMapping = poAutoDetectColumns(headers);
      const rows = importPOLinesFromCSV(content, mapping, true);
      if (rows.length === 0) {
        setImportError('No valid rows in CSV.');
        return;
      }
      onImport(poImportRowsToLineItems(rows));
    } catch {
      setImportError('Could not read CSV file.');
    }
    e.target.value = '';
  };

  return (
    <div className="space-y-3 rounded-lg border border-dashed border-pink-200 bg-pink-50/40 p-4">
      <p className="text-sm text-gray-700">
        Paste one product per line: <span className="font-mono text-xs">name, qty, cost</span> — or upload a CSV.
      </p>
      <textarea
        value={pasteText}
        onChange={(e) => setPasteText(e.target.value)}
        rows={5}
        placeholder={'Chicken breast, 10, 12.50\nOlive oil 5L, 2, 28.00'}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handlePasteImport}
          className="rounded-md bg-pink-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-pink-700"
        >
          Add pasted lines
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Upload CSV
        </button>
        <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} />
      </div>
      {importError ? <p className="text-sm text-red-600">{importError}</p> : null}
    </div>
  );
};

export default POPasteImportPanel;
